import { signal } from '@angular/core';

/**
 * Global viewport signal for desktop/tablet breakpoint (min-width: 720px)
 * 
 * Usage:
 * - Import: `import { isDesktopViewport } from '@shared/utils/viewport.utils';`
 * - In template: `@if (isDesktopViewport()) { ... }`
 * - In component: `const isDesktop = isDesktopViewport();`
 * - Reactive: `effect(() => { console.log('Desktop mode:', isDesktopViewport()); });`
 */
export const isDesktopViewport = signal<boolean>(false);

/**
 * Viewport breakpoints constants
 */
export const VIEWPORT_BREAKPOINTS = {
  MOBILE_MAX: 719,
  DESKTOP_MIN: 720,
  TABLET_MIN: 768,
  LAPTOP_MIN: 1024,
  DESKTOP_LARGE_MIN: 1440
} as const;

/**
 * Utility function to check if current viewport matches desktop breakpoint
 * Note: This is a one-time check, use the signal for reactive behavior
 */
export function checkIsDesktopViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= VIEWPORT_BREAKPOINTS.DESKTOP_MIN;
}

/**
 * Utility function to get current viewport category
 */
export function getCurrentViewportCategory(): 'mobile' | 'desktop' | 'tablet' | 'laptop' | 'desktop-large' {
  if (typeof window === 'undefined') return 'mobile';
  
  const width = window.innerWidth;
  
  if (width >= VIEWPORT_BREAKPOINTS.DESKTOP_LARGE_MIN) return 'desktop-large';
  if (width >= VIEWPORT_BREAKPOINTS.LAPTOP_MIN) return 'laptop';
  if (width >= VIEWPORT_BREAKPOINTS.TABLET_MIN) return 'tablet';
  if (width >= VIEWPORT_BREAKPOINTS.DESKTOP_MIN) return 'desktop';
  return 'mobile';
}

/**
 * Initialize the global viewport signals and start listening to resize events.
 * Returns a cleanup function that removes the listener.
 */
export function initViewportSignals(): () => void {
  if (typeof window === 'undefined') return () => {};

  // Keep a debounce timer to avoid flooding updates during continuous resize
  let resizeTimer: any = null;

  const update = () => {
    isDesktopViewport.set(checkIsDesktopViewport());
  };

  const onResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      update();
    }, 120);
  };

  // Set initial value
  update();

  window.addEventListener('resize', onResize);

  return () => {
    window.removeEventListener('resize', onResize);
    if (resizeTimer) clearTimeout(resizeTimer);
  };
}

/**
 * Convenience wrapper to get the current boolean desktop value easily
 */
export function isDesktop(): boolean {
  return isDesktopViewport();
}